import { useState } from 'react';
import { useApiClient } from './useApiClient';
import { useMsal } from '@azure/msal-react';
import { UserProfile } from '../types/userProfile';
import { envConfig, loginRequest } from '../config/authConfig';

const API_BASE_URL = envConfig.apiBaseUrl;

// Cache for user profile - stored in memory to avoid unnecessary API calls
// This cache persists until the page is refreshed or the user signs out
let cachedUserProfile: UserProfile | null = null;
let cachedPhotoUrl: string | null = null;
let photoFetchAttempted = false; // Track if we already tried fetching photo
let isFetching = false;
let isPhotoFetching = false;
let fetchPromise: Promise<UserProfile | null> | null = null;
let photoFetchPromise: Promise<string | null> | null = null;

// Function to clear cache on sign out
export const clearUserProfileCache = () => {
  cachedUserProfile = null;
  cachedPhotoUrl = null;
  photoFetchAttempted = false;
  isFetching = false;
  isPhotoFetching = false;
  fetchPromise = null;
  photoFetchPromise = null;
  console.log('User profile cache cleared');
};

// Get cached photo URL (for use by useAuth)
export const getCachedPhotoUrl = () => cachedPhotoUrl;

// Set cached photo URL (for use by useAuth when it fetches first)
export const setCachedPhotoUrl = (url: string | null) => {
  cachedPhotoUrl = url;
};

export const useUserProfile = () => {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(cachedUserProfile);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { apiCall } = useApiClient();
  const { instance, accounts } = useMsal();

  // Fetch profile photo from Microsoft Graph (with deduplication)
  const fetchProfilePhoto = async (): Promise<string | null> => {
    // Return cached photo if available
    if (cachedPhotoUrl) return cachedPhotoUrl;
    
    // If we already tried and failed, don't retry
    if (photoFetchAttempted) return null;
    
    // If already fetching, wait for the existing promise
    if (isPhotoFetching && photoFetchPromise) {
      return photoFetchPromise;
    }
    
    const account = accounts[0];
    if (!account) return null;

    isPhotoFetching = true;
    photoFetchAttempted = true; // Mark that we've attempted to fetch
    photoFetchPromise = (async () => {
      try {
        const response = await instance.acquireTokenSilent({
          ...loginRequest,
          account: account,
        });

        const graphResponse = await fetch(`${envConfig.graphApiUrl}/me/photo/$value`, {
          headers: {
            Authorization: `Bearer ${response.accessToken}`,
          },
        });

        if (graphResponse.ok) {
          const blob = await graphResponse.blob();
          const url = URL.createObjectURL(blob);
          cachedPhotoUrl = url;
          return url;
        }
        // 404 means no photo - this is expected, don't log as error
        if (graphResponse.status === 404) {
          return null;
        }
      } catch (error) {
        // Only log unexpected errors
        console.log('Profile photo fetch error:', error);
      } finally {
        isPhotoFetching = false;
      }
      return null;
    })();

    return photoFetchPromise;
  };

  const fetchUserProfile = async () => {
    // If we already have cached data, use it immediately
    if (cachedUserProfile) {
      console.log('Using cached user profile');
      setUserProfile(cachedUserProfile);
      return cachedUserProfile;
    }

    // If a fetch is already in progress, wait for it
    if (isFetching && fetchPromise) {
      console.log('Fetch already in progress, waiting...');
      const result = await fetchPromise;
      setUserProfile(result);
      return result;
    }

    setLoading(true);
    setError(null);
    isFetching = true;

    fetchPromise = (async () => {
      try {
        const response = await apiCall(`${API_BASE_URL}/api/user/profile`);
        
        if (response.status === 403) {
          const errorData = await response.json().catch(() => null);
          throw new Error(errorData?.message || 'Access restricted. You are not authorized to use this environment.');
        }

        if (!response.ok) {
          throw new Error(`Failed to fetch user profile: ${response.statusText}`);
        }

        const apiResponse = await response.json();
        
        // The backend returns ApiResponse<UserProfile> with the profile in the 'data' property
        if (apiResponse.success && apiResponse.data) {
          // Fetch profile photo from MS Graph
          const photoUrl = await fetchProfilePhoto();
          
          // Combine profile data with photo
          const profileWithPhoto: UserProfile = {
            ...apiResponse.data,
            photoUrl: photoUrl || undefined
          };
          
          // Cache the profile
          cachedUserProfile = profileWithPhoto;
          setUserProfile(profileWithPhoto);
          
          // Log the response to console for inspection
          console.log('===== USER PROFILE DATA (fetched from API) =====');
          console.log('Full Profile:', profileWithPhoto);
          console.log('Employee Info:', {
            name: profileWithPhoto.displayName,
            email: profileWithPhoto.email,
            jobTitle: profileWithPhoto.jobTitle,
            department: profileWithPhoto.department,
            photoUrl: profileWithPhoto.photoUrl ? 'Available' : 'Not available'
          });
          console.log('Manager Info:', profileWithPhoto.manager || 'No manager found');
          console.log('Direct Reports:', profileWithPhoto.directReports || 'No direct reports');
          console.log('================================================');
          
          return profileWithPhoto;
        } else {
          throw new Error(apiResponse.message || 'Failed to fetch user profile');
        }

      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        console.error('Error fetching user profile:', err);
        return null;
      } finally {
        setLoading(false);
        isFetching = false;
      }
    })();

    return fetchPromise;
  };

  return {
    userProfile,
    loading,
    error,
    fetchUserProfile,
    clearCache: clearUserProfileCache
  };
};
